import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, Eye, Loader2 } from 'lucide-react';
import { adminApi, extractErrorMessage } from '@kardume/services';
import type {
  BenefitAudience,
  BenefitPreviewDto,
  BenefitTypeDto,
  BenefitWeekday,
  PromotionBenefitDto,
  PromotionBenefitRequestDto,
} from '@kardume/types';
import { BENEFIT_AUDIENCE_LABELS, BENEFIT_WEEKDAYS } from '@kardume/types';
import { Badge, Checkbox, CurrencyInput, Select, Switch, TextInput } from '@kardume/ui';

/**
 * Configuração do benefício de um anúncio (card #188).
 *
 * ## Por que os campos vêm do backend
 *
 * A lista de tipos e, principalmente, **quais campos cada tipo exige** chegam do
 * core (`GET /v1/merchant/promotions/benefit-types`). A tentação é embutir isso
 * aqui num `switch (typeCode)` - e é justamente o que tornaria o catálogo
 * administrável inútil: abrir "entrada VIP" no backoffice passaria a exigir um
 * deploy do painel. Com os metadados no contrato, tipo novo aparece já sabendo o
 * que pedir.
 *
 * ## Por que a pré-visualização é do backend
 *
 * A conta ("30% de R$ 90 = R$ 27") sai de `POST .../benefit-preview`. Simular
 * aqui daria ao painel uma segunda implementação da aritmética do motor, e o
 * lojista configuraria olhando um número que o balcão não vai repetir.
 */

const Wrapper = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.9rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.span`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Hint = styled.p`
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
  margin: 0;
`;

const WeekdayRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textSecondary};
`;

const PreviewCard = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 8px;
  padding: 0.85rem 1rem;
  display: grid;
  gap: 0.4rem;
  background: ${(p) => p.theme.surfaceMuted ?? p.theme.background};
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const PreviewLabel = styled.strong`
  font-size: 1rem;
  color: ${(p) => p.theme.text};
`;

const PreviewLine = styled.span`
  font-size: 0.82rem;
  color: ${(p) => p.theme.textSecondary};
`;

const PreviewError = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  color: ${(p) => p.theme.error};
`;

/** Conta de exemplo da pré-visualização, em reais. */
const DEFAULT_SAMPLE_PURCHASE = 100;

const AUDIENCE_OPTIONS = (Object.keys(BENEFIT_AUDIENCE_LABELS) as BenefitAudience[]).map((value) => ({
  value,
  label: BENEFIT_AUDIENCE_LABELS[value],
}));

const money = (value?: number | null): string =>
  value == null ? '' : value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** O benefício lido do core, no formato que o formulário envia de volta. */
export const benefitToDraft = (benefit?: PromotionBenefitDto | null): PromotionBenefitRequestDto | undefined => {
  if (!benefit) return undefined;
  return {
    typeCode: benefit.typeCode,
    percentValue: benefit.percentValue ?? undefined,
    amountValue: benefit.amountValue ?? undefined,
    paidUnits: benefit.paidUnits ?? undefined,
    freeUnits: benefit.freeUnits ?? undefined,
    itemLabel: benefit.itemLabel ?? undefined,
    maxDiscountAmount: benefit.maxDiscountAmount ?? undefined,
    minPurchaseAmount: benefit.minPurchaseAmount ?? undefined,
    weekdays: benefit.weekdays,
    startsTime: benefit.startsTime?.slice(0, 5) ?? undefined,
    endsTime: benefit.endsTime?.slice(0, 5) ?? undefined,
    audience: benefit.audience,
    returningMinRedemptions: benefit.returningMinRedemptions ?? undefined,
    requiredPassPlanId: benefit.requiredPassPlanId ?? undefined,
    perCustomerLimit: benefit.perCustomerLimit ?? undefined,
    flash: benefit.flash,
  };
};

interface BenefitInputProps {
  /** `undefined` = o anúncio ainda não tem benefício configurado. */
  value?: PromotionBenefitRequestDto;
  types: BenefitTypeDto[];
  typesLoading: boolean;
  typesError?: string | null;
  disabled?: boolean;
  onChange: (value: PromotionBenefitRequestDto | undefined) => void;
}

const BenefitInput: React.FC<BenefitInputProps> = ({
  value,
  types,
  typesLoading,
  typesError,
  disabled,
  onChange,
}) => {
  const [preview, setPreview] = useState<BenefitPreviewDto | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [samplePurchase, setSamplePurchase] = useState<number | undefined>(DEFAULT_SAMPLE_PURCHASE);

  const selectedType = useMemo(
    () => types.find((t) => t.code === value?.typeCode),
    [types, value?.typeCode]
  );

  const typeOptions = useMemo(
    () => [
      { value: '', label: 'Sem benefício configurado' },
      ...types.map((t) => ({ value: t.code, label: t.label })),
    ],
    [types]
  );

  const update = <K extends keyof PromotionBenefitRequestDto>(
    key: K,
    next: PromotionBenefitRequestDto[K]
  ) => {
    if (!value) return;
    onChange({ ...value, [key]: next });
  };

  /**
   * Trocar o tipo limpa os valores do tipo anterior e aplica os presets que o
   * catálogo sugere. Manter "20%" ao trocar de "desconto" para "brinde"
   * deixaria um número órfão viajando no payload até o core recusá-lo.
   */
  const handleTypeChange = (code: string) => {
    if (!code) {
      onChange(undefined);
      return;
    }
    const type = types.find((t) => t.code === code);
    onChange({
      typeCode: code,
      audience: type?.defaultAudience ?? 'EVERYONE',
      flash: type?.defaultFlash ?? false,
      weekdays: value?.weekdays,
      startsTime: value?.startsTime,
      endsTime: value?.endsTime,
      perCustomerLimit: value?.perCustomerLimit,
      minPurchaseAmount: value?.minPurchaseAmount,
    });
  };

  const toggleWeekday = (day: BenefitWeekday, checked: boolean) => {
    const current = value?.weekdays ?? [];
    const next = checked ? [...current, day] : current.filter((d) => d !== day);
    // Todos marcados e nenhum marcado dizem a mesma coisa ao core - sem
    // restrição -, então mandamos a lista vazia nos dois casos.
    update('weekdays', next.length === BENEFIT_WEEKDAYS.length ? [] : next);
  };

  const weekdaySelected = (day: BenefitWeekday): boolean => {
    const current = value?.weekdays ?? [];
    return current.length === 0 || current.includes(day);
  };

  /**
   * A pré-visualização é debounced e ignora resposta fora de ordem: o lojista
   * digita "3", "30", e a resposta do "3" pode voltar depois - mostrar o
   * desconto de 3% sobre um campo que já diz 30 é pior do que não mostrar nada.
   */
  const requestId = useRef(0);
  const payloadKey = JSON.stringify({ value, samplePurchase });

  useEffect(() => {
    if (!value?.typeCode) {
      setPreview(null);
      setPreviewError(null);
      return undefined;
    }
    const id = ++requestId.current;
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      adminApi
        .previewBenefit({ benefit: value, purchaseAmount: samplePurchase })
        .then((data) => {
          if (id !== requestId.current) return;
          setPreview(data);
          setPreviewError(null);
        })
        .catch((err) => {
          if (id !== requestId.current) return;
          setPreview(null);
          // O core devolve aqui o MESMO erro que o Salvar devolveria - é o
          // ponto: o lojista vê o problema enquanto digita, não ao salvar.
          setPreviewError(extractErrorMessage(err, 'Não foi possível pré-visualizar o benefício.'));
        })
        .finally(() => {
          if (id === requestId.current) setPreviewLoading(false);
        });
    }, 400);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey]);

  const showsMoneySimulation =
    selectedType?.valueShape === 'PERCENT' ||
    selectedType?.valueShape === 'FIXED_AMOUNT' ||
    selectedType?.valueShape === 'CASHBACK_PERCENT';

  return (
    <Wrapper>
      <Select
        label="Tipo de benefício"
        options={typeOptions}
        value={value?.typeCode ?? ''}
        disabled={disabled || typesLoading}
        onChange={(e) => handleTypeChange(e.target.value)}
        error={typesError ?? undefined}
      />
      {selectedType?.description && <Hint>{selectedType.description}</Hint>}
      {!value && (
        <Hint>
          Sem benefício configurado o anúncio continua no ar e resgatável, mostrando apenas o texto
          que você escreveu. Escolha um tipo para o app dizer exatamente o que o cliente ganha.
        </Hint>
      )}

      {value && selectedType && (
        <>
          <Row>
            {selectedType.requiresPercent && (
              <TextInput
                label="Percentual (%)"
                type="number"
                min={0}
                max={100}
                disabled={disabled}
                value={value.percentValue ?? ''}
                onChange={(e) =>
                  update('percentValue', e.target.value === '' ? undefined : Number(e.target.value))
                }
              />
            )}
            {selectedType.requiresAmount && (
              <CurrencyInput
                label="Valor do benefício"
                disabled={disabled}
                value={value.amountValue}
                onChange={(v) => update('amountValue', v)}
              />
            )}
            {selectedType.requiresUnits && (
              <>
                <TextInput
                  label="O cliente paga (unidades)"
                  type="number"
                  min={1}
                  disabled={disabled}
                  value={value.paidUnits ?? ''}
                  onChange={(e) =>
                    update('paidUnits', e.target.value === '' ? undefined : Number(e.target.value))
                  }
                />
                <TextInput
                  label="E leva (unidades)"
                  type="number"
                  min={1}
                  disabled={disabled}
                  value={value.freeUnits ?? ''}
                  onChange={(e) =>
                    update('freeUnits', e.target.value === '' ? undefined : Number(e.target.value))
                  }
                />
              </>
            )}
            {selectedType.requiresItemLabel && (
              <TextInput
                label="O que o cliente recebe"
                placeholder="Ex.: sobremesa da casa"
                maxLength={120}
                disabled={disabled}
                value={value.itemLabel ?? ''}
                onChange={(e) => update('itemLabel', e.target.value || undefined)}
              />
            )}
          </Row>

          <Row>
            <CurrencyInput
              label="Compra mínima"
              helpText="Deixe em branco para valer em qualquer valor."
              disabled={disabled}
              value={value.minPurchaseAmount}
              onChange={(v) => update('minPurchaseAmount', v)}
            />
            {selectedType.valueShape === 'PERCENT' && (
              <CurrencyInput
                label="Teto de desconto"
                helpText="Não pode passar da compra mínima - senão o desconto ultrapassa a conta."
                disabled={disabled}
                value={value.maxDiscountAmount}
                onChange={(v) => update('maxDiscountAmount', v)}
              />
            )}
          </Row>

          <div>
            <SectionTitle>Dias em que vale</SectionTitle>
            <Hint>Todos marcados = sem restrição de dia.</Hint>
            <WeekdayRow>
              {BENEFIT_WEEKDAYS.map((day) => (
                <Checkbox
                  key={day.value}
                  label={day.label}
                  disabled={disabled}
                  checked={weekdaySelected(day.value)}
                  onChange={(e) => toggleWeekday(day.value, e.target.checked)}
                />
              ))}
            </WeekdayRow>
          </div>

          <Row>
            <TextInput
              label="A partir das"
              type="time"
              disabled={disabled}
              value={value.startsTime ?? ''}
              onChange={(e) => update('startsTime', e.target.value || undefined)}
            />
            <TextInput
              label="Até as"
              type="time"
              disabled={disabled}
              value={value.endsTime ?? ''}
              onChange={(e) => update('endsTime', e.target.value || undefined)}
            />
          </Row>

          <Row>
            <Select
              label="Quem pode resgatar"
              options={AUDIENCE_OPTIONS}
              disabled={disabled}
              value={value.audience ?? 'EVERYONE'}
              onChange={(e) => update('audience', e.target.value as BenefitAudience)}
            />
            {value.audience === 'RETURNING' && (
              <TextInput
                label="A partir de quantos resgates na loja"
                type="number"
                min={1}
                disabled={disabled}
                value={value.returningMinRedemptions ?? ''}
                onChange={(e) =>
                  update(
                    'returningMinRedemptions',
                    e.target.value === '' ? undefined : Number(e.target.value)
                  )
                }
              />
            )}
          </Row>

          <Row>
            <div>
              <TextInput
                label="Limite por pessoa"
                type="number"
                min={1}
                disabled={disabled}
                value={value.perCustomerLimit ?? ''}
                onChange={(e) =>
                  update('perCustomerLimit', e.target.value === '' ? undefined : Number(e.target.value))
                }
              />
              {/* `TextInput` não tem `helpText` (só o `CurrencyInput` tem) - a
                  dica fica ao lado em vez de virar prop nova no design system. */}
              <Hint>Quantas vezes o mesmo cliente pode resgatar. Em branco = sem limite.</Hint>
            </div>
            <SwitchRow>
              <Switch
                checked={!!value.flash}
                disabled={disabled}
                onChange={(v) => update('flash', v)}
              />
              Relâmpago (mostra contagem regressiva no app)
            </SwitchRow>
          </Row>

          <PreviewCard>
            <PreviewHeader>
              {previewLoading ? <Loader2 size={14} /> : <Eye size={14} />} Como o cliente vê
            </PreviewHeader>
            {previewError && (
              <PreviewError>
                <AlertTriangle size={14} /> {previewError}
              </PreviewError>
            )}
            {!previewError && preview && (
              <>
                <PreviewLabel>{preview.customerLabel}</PreviewLabel>
                {preview.windowLabel && <PreviewLine>{preview.windowLabel}</PreviewLine>}
                {preview.audienceLabel && (
                  <PreviewLine>
                    <Badge tone="secondary">{preview.audienceLabel}</Badge>
                  </PreviewLine>
                )}
                {showsMoneySimulation && (
                  <>
                    <CurrencyInput
                      label="Simular uma conta de"
                      value={samplePurchase}
                      disabled={disabled}
                      onChange={setSamplePurchase}
                    />
                    {preview.belowMinimumPurchase && (
                      <PreviewLine>
                        Esta conta não atinge a compra mínima: o cliente não teria o benefício.
                      </PreviewLine>
                    )}
                    {preview.discountAmount != null && (
                      <PreviewLine>
                        Desconto de R$ {money(preview.discountAmount)} - o cliente paga R${' '}
                        {money(preview.finalAmount)}.
                      </PreviewLine>
                    )}
                    {preview.cashbackAmount != null && (
                      <PreviewLine>Volta R$ {money(preview.cashbackAmount)} na carteira.</PreviewLine>
                    )}
                  </>
                )}
              </>
            )}
          </PreviewCard>
        </>
      )}
    </Wrapper>
  );
};

export default BenefitInput;
